import React, { forwardRef, useRef, useState } from 'react';
import { Animated, Text, TextStyle, TouchableOpacity, TouchableOpacityProps, ViewStyle } from 'react-native';

import { Theme } from '@constants';

import { styles } from './styles';

export const TWCSwitch = forwardRef<TouchableOpacity, TWCSwitchProps>(
  (
    {
      value: propsValue = false,
      onValueChange = () => null,
      renderInsideCircle = () => null,
      activeText = 'On',
      inActiveText = 'Off',
      backgroundActive = 'green',
      backgroundInactive = 'gray',
      circleActiveColor = Theme.white,
      circleInActiveColor = Theme.white,
      circleBorderActiveColor = Theme.suvaGrey,
      circleBorderInactiveColor = Theme.rhinoGrey,
      circleSize = 30,
      barHeight,
      circleBorderWidth = 1,
      changeValueImmediately = true,
      innerCircleStyle = { alignItems: 'center', justifyContent: 'center' },
      renderActiveText = true,
      renderInActiveText = true,
      switchLeftPx = 2,
      switchRightPx = 2,
      switchWidthMultiplier = 2,
      outerCircleStyle,
      containerStyle,
      switchBorderRadius,
      activeTextStyle,
      inactiveTextStyle,
      ...restProperties
    },
    ref,
  ) => {
    const [value, setValue] = useState(propsValue);
    const transformSwitch = useRef(
      new Animated.Value(propsValue ? circleSize / switchLeftPx : -circleSize / switchRightPx),
    ).current;
    const backgroundColor = useRef(new Animated.Value(propsValue ? 75 : -75)).current;
    const circleColor = useRef(new Animated.Value(propsValue ? 75 : -75)).current;
    const circleBorderColor = useRef(new Animated.Value(propsValue ? 75 : -75)).current;

    const handleSwitch = () => {
      if (propsValue === value) {
        onValueChange(!value);
        return;
      }

      if (changeValueImmediately) {
        animateSwitch(!propsValue);
        onValueChange(!propsValue);
      } else {
        animateSwitch(!value, () => setValue(val => !val));
        onValueChange(!value);
      }
    };

    const animateSwitch = (incomeValue: boolean, callback = () => {}) => {
      Animated.parallel([
        Animated.spring(transformSwitch, {
          toValue: incomeValue ? circleSize / switchLeftPx : -circleSize / switchRightPx,
          useNativeDriver: false,
        }),
        Animated.timing(backgroundColor, {
          toValue: incomeValue ? 75 : -75,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(circleColor, {
          toValue: incomeValue ? 75 : -75,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(circleBorderColor, {
          toValue: incomeValue ? 75 : -75,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start(callback);
    };

    const interpolatedColorAnimation = backgroundColor.interpolate({
      inputRange: [-75, 75],
      outputRange: [backgroundInactive, backgroundActive],
    });

    const interpolatedCircleColor = circleColor.interpolate({
      inputRange: [-75, 75],
      outputRange: [circleInActiveColor, circleActiveColor],
    });

    const interpolatedCircleBorderColor = circleBorderColor.interpolate({
      inputRange: [-75, 75],
      outputRange: [circleBorderInactiveColor, circleBorderActiveColor],
    });

    return (
      <TouchableOpacity activeOpacity={1} ref={ref} onPress={handleSwitch} {...restProperties}>
        <Animated.View
          style={[
            styles.container,
            containerStyle,
            {
              backgroundColor: interpolatedColorAnimation,
              width: circleSize * switchWidthMultiplier,
              height: barHeight || circleSize,
              borderRadius: switchBorderRadius || circleSize,
            },
          ]}
        >
          <Animated.View
            style={[
              styles.animatedContainer,
              {
                left: transformSwitch,
                width: circleSize * switchWidthMultiplier,
              },
              outerCircleStyle,
            ]}
          >
            {value && renderActiveText && (
              <Text style={[styles.text, styles.paddingRight, activeTextStyle]}>{activeText}</Text>
            )}

            <Animated.View
              style={[
                styles.circle,
                {
                  borderWidth: circleBorderWidth,
                  borderColor: interpolatedCircleBorderColor,
                  backgroundColor: interpolatedCircleColor,
                  width: circleSize,
                  height: circleSize,
                  borderRadius: circleSize / 2,
                },
                styles.circleStyle,
                value && styles.circleStyleValue,
                innerCircleStyle,
              ]}
            >
              {renderInsideCircle()}
            </Animated.View>
            {!value && renderInActiveText && (
              <Text style={[styles.text, styles.paddingLeft, inactiveTextStyle]}>{inActiveText}</Text>
            )}
          </Animated.View>
        </Animated.View>
      </TouchableOpacity>
    );
  },
);

export type TWCSwitchProps = TouchableOpacityProps & {
  onValueChange?: (value?: boolean) => void;
  activeText?: string;
  inActiveText?: string;
  backgroundActive?: string;
  backgroundInactive?: string;
  value?: boolean;
  circleActiveColor?: string;
  circleInActiveColor?: string;
  circleSize?: number;
  circleBorderActiveColor?: string;
  circleBorderInactiveColor?: string;
  activeTextStyle?: TextStyle;
  inactiveTextStyle?: TextStyle;
  containerStyle?: ViewStyle;
  barHeight?: number;
  circleBorderWidth?: number;
  innerCircleStyle?: ViewStyle;
  renderInsideCircle?: () => Element;
  changeValueImmediately?: boolean;
  outerCircleStyle?: ViewStyle;
  renderActiveText?: boolean;
  renderInActiveText?: boolean;
  switchLeftPx?: number;
  switchRightPx?: number;
  switchWidthMultiplier?: number;
  switchBorderRadius?: number;
  testProps?: string; // IDK WHAT IS IT
  nativeID?: string;
  onKeyDown?: (event: { key: string }) => void; // maybe not exist
};
